import { ActionDataTypes, ActionDataWidgets } from '../types'
import { Autocomplete, Box, Stack, TextArea, TextField } from '@dtx-company/design-system/src'
import {
  COUNTRY_CODES,
  DEFAULT_COUNTRY_CODE
} from '@dtx-company/true-common/src/constants/countryCodes'
import { DefaultManager, ManagerProps } from '../components/Manager/Manager'
import { DisplayToggle } from '../components/DisplayToggle'
import { FC, useEffect, useMemo, useState } from 'react'
import { FlowpageLinkCard } from '../../flowpage/FlowpageLinkCard'
import { FlowpageProps } from '../types/FlowpageProps'
import { FormProps, StyledFormV2 } from '../components/Layout'
import { FormTitle } from '../components/Title'
import { ImageEdit } from '../components/ImageEdit'
import { LinkProvider } from '@dtx-company/inter-app/src/constants/linkTypes'
import { LinkStyleGated } from '../components/LinkStyle/LinkStyleGated'
import { Message } from '@dtx-company/shared-components/src/foundation/Icons/Flowpage/Widgets/Message'
import { SettingsAndSaveLink } from '../components/Settings'
import { getValidatedActionData } from '../typeUtils'
import { isValidCSVEmails } from '../../../constants/customValidations'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useForm } from 'react-hook-form'
import { useLinkThemeState } from '../components/LinkStyle/hooks/useLinkThemeState'
import { usePreviousValue } from '@dtx-company/true-common/src/hooks/usePreviousValue/usePreviousValue'
import { useSetValueWithPreview } from '../utils'
import { useSubmitWidget } from '../submitUtils'
import { v4 as uuid } from 'uuid'

enum MessageType {
  SMS = 'SMS',
  EMAIL = 'Email',
  WHATSAPP = 'Whatsapp',
  CALL = 'Call'
}

enum FormFields {
  TYPE = 'type',
  NAME = 'name',
  IMAGE = 'image',
  SEND_TO = 'sendTo',
  BODY = 'body',
  SUBJECT = 'subject'
}

export const Form: FC<FormProps> = ({ widgetObj, order, curr, handleClose }: FormProps) => {
  const id = curr ? curr.id : uuid()
  const actionData = getValidatedActionData<'sendMessage'>(curr?.actionData, 'link')

  const firstCountryCode =
    COUNTRY_CODES.find(code => {
      return actionData?.countryCode === code.phone
    }) ?? DEFAULT_COUNTRY_CODE

  const [countryCode, setCountryCode] = useState(firstCountryCode)

  const { linkTheme, setLinkTheme } = useLinkThemeState({
    defaultLinkTheme: curr?.linkTheme ?? null
  })
  const { hasOperation } = useAuthState()
  const hasLibertyMutualOnlyLinks = hasOperation('view:liberty_mutual_links_only')
  const defaultMessageType = hasLibertyMutualOnlyLinks ? MessageType.CALL : MessageType.SMS
  const {
    register,
    setValue: setFormValue,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
    clearErrors
  } = useForm({
    defaultValues: {
      type: actionData?.type || defaultMessageType,
      link: '',
      name: curr?.title || '',
      image: curr?.thumbNailImgUrl || '',
      sendTo: actionData?.sendTo || '',
      subject: actionData?.subject || '',
      body: actionData?.body || ''
    }
  })

  const submitWidget = useSubmitWidget()

  const watchAll = watch()
  const isEmail = watchAll.type === MessageType.EMAIL
  const wasEmail = usePreviousValue(isEmail)
  const isCall = watchAll.type === MessageType.CALL
  const isPhoneNum =
    isCall || watchAll.type === MessageType.SMS || watchAll.type === MessageType.WHATSAPP

  const onSubmit = async ({
    type,
    name,
    image,
    sendTo,
    subject,
    body
  }: ActionDataTypes['sendMessage']): Promise<void> => {
    const actionData: ActionDataTypes['sendMessage'] = {
      type,
      name,
      sendTo,
      body,
      link: ''
    }

    const emailMessage = []

    const phone = countryCode?.phone?.replaceAll(' ', '')?.replaceAll('-', '')

    if (isEmail) {
      actionData.subject = subject
      if (subject) emailMessage.push(`subject=${subject}`)
      if (body) emailMessage.push(`body=${encodeURIComponent(body)}`)
      const cleanedSendTo = sendTo
        .split(',')
        .map(email => email.trim())
        .join(',')
      actionData.link = `mailto:${cleanedSendTo}?${emailMessage.join('&')}`
    } else if (phone) {
      actionData.countryCode = phone
      const nextSendTo = sendTo.replaceAll(' ', '').replaceAll('-', '')

      const nextLink = (() => {
        if (type === MessageType.SMS) {
          const url = new URL(`sms:${phone ? `+${phone}` : ''}${nextSendTo}`)
          return `${url}?&body=${window.encodeURIComponent(body ?? '')}`
        }

        if (type === MessageType.CALL) {
          return phone ? `tel:+${phone}${nextSendTo}` : `tel:${nextSendTo}`
        }

        if (type === MessageType.WHATSAPP) {
          return `https://api.whatsapp.com/send?phone=+${phone}${nextSendTo}&text=${body}`
        }

        return ''
      })()

      actionData.link = nextLink
    }

    await submitWidget({
      image,
      curr,
      actionData: { ...actionData },
      linkTheme,
      handleClose,
      widgetType: (curr ? curr.type : widgetObj.type) as ActionDataWidgets,
      fields: {
        title: name,
        description: '',
        id,
        order
      }
    })
  }

  useEffect(() => {
    register(FormFields.TYPE)
    register(FormFields.NAME, {
      required: 'Please enter a title'
    })
    register(FormFields.IMAGE)
    register(FormFields.SEND_TO, {
      validate: (value: string) => {
        if (isEmail) {
          return (
            isValidCSVEmails(value) || 'One or more of the emails entered is invalid or incomplete'
          )
        }
        return Boolean(value.length) || 'Please enter a valid phone number'
      },
      required: `Please enter ${isEmail ? 'at least one email' : 'a phone number'}`
    })
    register(FormFields.BODY, {
      maxLength: {
        value: 160,
        message: "You can't use more than 160 characters"
      }
    })
    register(FormFields.SUBJECT, {
      maxLength: {
        value: 78,
        message: "You can't use more than 78 characters"
      }
    })
  }, [isEmail, register])

  const previewLink = useMemo(
    () => ({
      provider: LinkProvider.WIDGET,
      type: 'sendMessage',
      expand: null,
      __typename: 'Link' as const,
      actionData: actionData || {},
      title: watchAll.name,
      thumbNailImgUrl: watchAll.image,
      id,
      linkTheme,
      order,
      active: true,
      embed: false,
      description: '',
      displayType: 'default'
    }),
    [actionData, id, linkTheme, order, watchAll.image, watchAll.name]
  )
  const disabled = !!(errors?.name || errors?.sendTo)
  const edit = Boolean(curr)
  const { setValue, setLinkThemeValue } = useSetValueWithPreview(
    previewLink,
    setFormValue,
    setLinkTheme
  )
  return (
    <StyledFormV2 onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={4}>
        <FormTitle icon={<Message />} title={`${edit ? 'Edit this' : 'Add a'} message`} />
        <DisplayToggle
          hideLabel
          displayOptions={[
            { title: 'SMS', value: 'SMS' },
            { title: 'Call', value: 'Call' },
            { title: 'Whatsapp', value: 'Whatsapp' },
            { title: 'Email', value: 'Email' }
          ]}
          displayType={watchAll.type}
          onChange={(_e, type) => {
            setValue(FormFields.TYPE, type)
            if (type === MessageType.EMAIL || wasEmail) {
              setFormValue(FormFields.SEND_TO, '')
              clearErrors(FormFields.SEND_TO)
            }
          }}
        />
        <TextField
          fullWidth
          label="Title"
          placeholder="Enter a title"
          onChange={e =>
            setValue(FormFields.NAME, e.target.value, {
              shouldValidate: true
            })
          }
          onBlur={() => trigger(FormFields.NAME)}
          value={watchAll.name}
          helperText={errors?.name?.message}
          error={Boolean(errors.name)}
        />
        <ImageEdit
          imageUrl={watchAll.image || '/icons/messages.svg'}
          setValue={setValue}
          defaultImage="/icons/messages.svg"
        />
        {isEmail && (
          <>
            <TextField
              fullWidth
              label="Email(s)"
              placeholder="Enter email address(es)"
              onChange={e =>
                setFormValue(FormFields.SEND_TO, e.target.value, {
                  shouldValidate: true
                })
              }
              onBlur={() => trigger(FormFields.SEND_TO)}
              value={watchAll.sendTo}
              helperText={
                errors?.sendTo?.message || 'If using multiple emails separate with commas(,)'
              }
              error={Boolean(errors.sendTo)}
            />
            <TextField
              fullWidth
              label="Subject"
              placeholder="Enter a subject"
              onChange={e =>
                setValue(FormFields.SUBJECT, e.target.value, {
                  shouldValidate: true
                })
              }
              onBlur={() => trigger(FormFields.SUBJECT)}
              value={watchAll.subject}
              helperText={errors?.subject?.message}
              error={Boolean(errors.subject)}
            />
          </>
        )}
        {isPhoneNum && (
          <Stack direction="row">
            <Stack flexGrow={1}>
              <Autocomplete
                disablePortal
                defaultValue={DEFAULT_COUNTRY_CODE}
                value={countryCode}
                onChange={(_, v) => {
                  if (v) {
                    setCountryCode(v)
                  }
                }}
                options={COUNTRY_CODES}
                fullWidth
                getOptionLabel={option => `+${option.phone} ${option.code}`}
                renderInput={params => (
                  <TextField {...params} label="Country code" fullWidth hiddenLabel />
                )}
              />
            </Stack>
            <Stack flexGrow={2}>
              <TextField
                type="tel"
                value={watchAll.sendTo}
                error={Boolean(errors.sendTo)}
                helperText={errors?.sendTo?.message}
                label="Phone number"
                onChange={e =>
                  setFormValue(FormFields.SEND_TO, e.target.value, {
                    shouldValidate: true
                  })
                }
                fullWidth
              />
            </Stack>
          </Stack>
        )}
        {!isCall && (
          <TextArea
            label={isEmail ? 'Body' : 'Message'}
            placeholder={`Enter ${isEmail ? 'body message' : 'a message'}`}
            onChange={e => setValue(FormFields.BODY, e.target.value, { shouldValidate: true })}
            onBlur={() => trigger(FormFields.BODY)}
            value={watchAll.body}
            helperText={
              <Stack direction="row">
                {errors?.body?.message ? <Box>{errors.body.message}</Box> : null}
                <Box
                  sx={{ width: '100%', textAlign: 'right !important' }}
                >{`${watchAll.body.length} / ${160}`}</Box>
              </Stack>
            }
            error={Boolean(errors.body)}
          />
        )}
        <LinkStyleGated linkTheme={linkTheme} setLinkTheme={setLinkThemeValue} />
        <SettingsAndSaveLink disabled={disabled} curr={curr} handleClose={handleClose} />{' '}
      </Stack>
    </StyledFormV2>
  )
}

export const Flowpage: FC<FlowpageProps> = ({
  link,
  editLinkMode,
  preview,
  isLockedTemplateLink
}: FlowpageProps) => {
  return (
    <FlowpageLinkCard
      link={link}
      isLockedTemplateLink={isLockedTemplateLink}
      widgetId={'message'}
      preview={preview}
      iconSrc={link.thumbNailImgUrl || '/icons/messages.svg'}
      onlyTitle={false}
      editLinkMode={editLinkMode}
    />
  )
}

export const Manager: FC<ManagerProps> = ({ editWidget, link, handle }: ManagerProps) => {
  return (
    <DefaultManager
      iconUrl={'/icons/messages.svg'}
      handle={handle}
      editWidget={editWidget}
      link={link}
    />
  )
}
